import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { PatientInfoValues } from '../types/flow';
import NavRow from '../../assessment-portal/components/navigation-row';
import PersonalContactForm from '../components/personal-contact-form';
import EmergencyMedicareForm from '../components/emergency-medicare-form';
import { isEmergencyMedicareComplete, isPersonalInfoComplete } from '../utils/details-validation';

interface DetailsPageProps {
  defaultValues: PatientInfoValues;
  onBack: () => void;
  onNext: (_values: PatientInfoValues) => void;
  /** The personal and contact tab is done: the draft booking is saved from it. */
  onPersonalInfoComplete?: (_values: PatientInfoValues) => void;
}

const DetailsPage: FC<DetailsPageProps> = ({
  defaultValues,
  onBack,
  onNext,
  onPersonalInfoComplete,
}) => {
  const [detailStep, setDetailStep] = useState(0);

  const methods = useForm<PatientInfoValues>({
    defaultValues: defaultValues || {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      email: '',
      mobile: '',
      address: '',
      suburb: '',
      state: '',
      postcode: '',
      cultural: '',
      referral: '',
      emergencyName: '',
      emergencyRelationship: '',
      emergencyMobile: '',
      medicareType: '',
      medicareNumber: '',
      medicareRef: '',
      medicareExpiry: '',
      dvaNumber: '',
      dvaCardType: '',
    },
    mode: 'onChange',
  });

  const {
    control, watch, setValue, getValues, handleSubmit,
  } = methods;
  const values = watch();

  const personalComplete = isPersonalInfoComplete(values);
  const emergencyComplete = isEmergencyMedicareComplete(values);
  const tabComplete = [personalComplete, emergencyComplete];

  // The only way onto Emergency & Medicare, from its Next button or its tab
  // header: the personal details must be complete, and leaving them saves
  // the draft booking.
  const handlePersonalInfoNext = () => {
    if (!isPersonalInfoComplete(getValues())) return;
    onPersonalInfoComplete?.(getValues());
    setDetailStep(1);
  };

  const handleTabClick = (step: number) => {
    if (step === 0) {
      setDetailStep(0);
      return;
    }
    handlePersonalInfoNext();
  };

  const handleFinalSubmit = handleSubmit((data) => {
    // Its fields are not mounted on this tab, so the form cannot check them.
    if (!isPersonalInfoComplete(data)) {
      setDetailStep(0);
      return;
    }
    onNext(data);
  });

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-none md:rounded-2xl shadow-sm border border-slate-200 p-4 md:p-8">
      <div className="flex gap-2 mb-6">
        {['Personal & Contact', 'Emergency & Medicare'].map((t, i) => {
          const active = detailStep === i;
          // Emergency & Medicare cannot be opened before the personal details are done.
          const locked = i === 1 && !personalComplete;
          return (
            <button
              key={t}
              type="button"
              onClick={() => handleTabClick(i)}
              disabled={locked}
              aria-disabled={locked}
              className={`flex-1 py-2 rounded-xl text-tab-label md:text-xs font-bold border-2 transition-all leading-tight ${
                active
                  ? 'border-brand bg-brand bg-opacity-5 text-brand'
                  : 'border-slate-200 bg-white text-slate-400'
              } ${locked ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              {!active && tabComplete[i] ? '✓ ' : ''}
              {t}
            </button>
          );
        })}
      </div>

      {detailStep === 0 && (
        <>
          <PersonalContactForm
            control={control}
          />

          <NavRow
            onBack={onBack}
            onNext={handlePersonalInfoNext}
            nextDisabled={!personalComplete}
            nextLabel="Next: Emergency & Medicare →"
          />
        </>
      )}

      {detailStep === 1 && (
        <>
          <EmergencyMedicareForm
            control={control}
            values={values}
            setValue={setValue}
          />

          <NavRow
            onBack={() => setDetailStep(0)}
            onNext={handleFinalSubmit}
            nextDisabled={!personalComplete || !emergencyComplete}
            nextLabel="Continue to Fee Estimate →"
          />
        </>
      )}
    </div>
  );
};

export default DetailsPage;
